import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  startAgentSession,
  sendSessionMessage,
  getJobStatus,
  getSessionHistory,
  listSessions,
} from '../api';
import { pollJobStatus } from '../utils/jobPolling';
import { useJobActivity } from '../hooks/useJobActivity';
import { AgentCardList, parseAgentOutput } from '../components/AgentCards';
import ConistraMark from '../components/ConistraMark';
import type { AgentMessage, AgentAction, PageKey } from '../types';
import './AssistantChatPage.css';

type AssistantChatPageProps = {
  workspaceId: number;
};

type SessionEntry = {
  session_id: string;
  created_at: string;
};

/** Extends AgentMessage to hold the parsed card actions from the agent JSON response. */
interface BrainMessage extends AgentMessage {
  cardActions?: AgentAction[];
}

const WELCOME_MESSAGE: BrainMessage = {
  role: 'assistant',
  message:
    "Hey — I'm the Oracle, your senior strategist. Tell me what you want and I'll run it: build a plan, schedule posts, create content, or just ask what's going on with your account.",
  actions: [],
};

/**
 * After this many user messages in a single session we nudge the user to start a
 * fresh conversation. Long sessions bloat the context window sent to the model on
 * every turn, which wastes tokens and degrades answer quality.
 */
const SESSION_MESSAGE_NUDGE_THRESHOLD = 10;

type SuggestionItem = { icon: string; text: string };

const SUGGESTIONS: SuggestionItem[] = [
  { icon: '📅', text: "What's my schedule looking like?" },
  { icon: '🗓️', text: 'Schedule my unscheduled posts' },
  { icon: '📝', text: 'Create a new 2-week content plan' },
  { icon: '✨', text: 'Produce content for my latest 3 outlines' },
  { icon: '🎨', text: 'Update my visual style to be more minimalist' },
];

const PAGE_PATHS: Record<PageKey, string> = {
  dashboard: '/',
  tasks: '/tasks',
  strategy: '/strategy',
  pillars: '/pillars',
  plans: '/plans',
  content: '/content',
  calendar: '/calendar',
  schedule: '/schedule',
  assistant: '/assistant',
  media: '/media',
  supervisor: '/agents/supervisor',
  engagement: '/agents/engagement',
  settings: '/settings',
  activate: '/activate',
};

function normalizeAssistantMessage(raw: string): string {
  return raw.replace(/\r\n?/g, '\n').replace(/\\n/g, '\n').replace(/\\t/g, '\t').trim();
}

function renderFormattedMessage(message: string): React.ReactNode {
  const normalized = normalizeAssistantMessage(message);
  const lines = normalized.split('\n');
  const nodes: React.ReactNode[] = [];
  let paragraphBuffer: string[] = [];
  let bulletBuffer: string[] = [];
  let orderedBuffer: string[] = [];

  const flushParagraph = () => {
    if (paragraphBuffer.length === 0) return;
    nodes.push(
      <p key={`p-${nodes.length}`} className="ob-bubble__paragraph">
        {paragraphBuffer.join(' ')}
      </p>
    );
    paragraphBuffer = [];
  };

  const flushBullets = () => {
    if (bulletBuffer.length === 0) return;
    nodes.push(
      <ul key={`ul-${nodes.length}`} className="ob-bubble__list">
        {bulletBuffer.map((item, idx) => (
          <li key={idx}>{item}</li>
        ))}
      </ul>
    );
    bulletBuffer = [];
  };

  const flushOrdered = () => {
    if (orderedBuffer.length === 0) return;
    nodes.push(
      <ol key={`ol-${nodes.length}`} className="ob-bubble__list ob-bubble__list--ordered">
        {orderedBuffer.map((item, idx) => (
          <li key={idx}>{item}</li>
        ))}
      </ol>
    );
    orderedBuffer = [];
  };

  for (const rawLine of lines) {
    const line = rawLine.trim();

    if (!line) {
      flushParagraph();
      flushBullets();
      flushOrdered();
      continue;
    }

    if (/^[-*]\s+/.test(line)) {
      flushParagraph();
      flushOrdered();
      bulletBuffer.push(line.replace(/^[-*]\s+/, ''));
      continue;
    }

    if (/^\d+\.\s+/.test(line)) {
      flushParagraph();
      flushBullets();
      orderedBuffer.push(line.replace(/^\d+\.\s+/, ''));
      continue;
    }

    flushBullets();
    flushOrdered();
    paragraphBuffer.push(line);
  }

  flushParagraph();
  flushBullets();
  flushOrdered();

  if (nodes.length === 0) {
    return normalized;
  }

  return nodes;
}

/** True when the given ISO timestamp falls on the local calendar day of `now`. */
function isSameLocalDay(isoString: string, now = new Date()): boolean {
  const d = new Date(isoString);
  return (
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate()
  );
}

function formatSessionRelative(isoString: string): string {
  const d = new Date(isoString);
  const now = Date.now();
  const diffMs = now - d.getTime();
  const diffMins = Math.floor(diffMs / 60_000);
  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHrs = Math.floor(diffMins / 60);
  if (diffHrs < 24) return `${diffHrs}h ago`;
  const diffDays = Math.floor(diffHrs / 24);
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function AssistantChatPage({ workspaceId }: AssistantChatPageProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const initialMessage =
    (location.state as { initialMessage?: string } | null)?.initialMessage ?? '';
  const initialMessageSentRef = useRef(false);

  const [messages, setMessages] = useState<BrainMessage[]>([WELCOME_MESSAGE]);
  const [input, setInput] = useState('');
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [initError, setInitError] = useState<string | null>(null);
  const [expandedDetails, setExpandedDetails] = useState<Record<number, boolean>>({});
  const [pastSessions, setPastSessions] = useState<SessionEntry[]>([]);
  const [showSessionPicker, setShowSessionPicker] = useState(false);
  const [nudgeDismissed, setNudgeDismissed] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const sessionPickerRef = useRef<HTMLDivElement>(null);
  const creatingSessionRef = useRef(false);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // The turn in flight, if any — its job feeds the pending bubble's live trail.
  const activitySteps = useJobActivity(
    messages.find((m) => m.status === 'pending')?.jobId ?? null
  );

  useEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 180)}px`;
  }, [input]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Close session picker on outside click
  useEffect(() => {
    if (!showSessionPicker) return;
    const handleClick = (e: MouseEvent) => {
      if (sessionPickerRef.current && !sessionPickerRef.current.contains(e.target as Node)) {
        setShowSessionPicker(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [showSessionPicker]);

  // Fetch past sessions on mount. We run one Brain session per calendar day: if a
  // session already exists for today we resume it instead of spinning up a new one,
  // so revisiting the Brain screen continues the same conversation. A new session is
  // only created lazily (on first user message) when none exists for today.
  useEffect(() => {
    listSessions(workspaceId, 'BRAIN')
      .then(({ sessions }) => {
        setPastSessions(sessions);
        // Skip auto-resume when arriving with a deep-linked message to send — that
        // flow drives session selection itself via ensureSession().
        if (initialMessage) return;
        // sessions are returned newest-first by the backend.
        const todays = sessions.find((s) => isSameLocalDay(s.created_at));
        if (todays) {
          loadSession(todays.session_id);
        }
      })
      .catch(() => {
        /* silently ignore */
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceId]);

  // Auto-send initialMessage passed via navigation state (e.g. from the calendar "create post" click).
  // We set the input and then trigger send via a small timeout so the component is fully mounted.
  useEffect(() => {
    if (!initialMessage || initialMessageSentRef.current) return;
    initialMessageSentRef.current = true;
    setInput(initialMessage);
    // Use a minimal delay so the input state is applied before we call send
    const t = setTimeout(() => {
      handleSend(initialMessage);
    }, 100);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialMessage]);

  const startNewSession = () => {
    setShowSessionPicker(false);
    creatingSessionRef.current = false;
    setNudgeDismissed(false);
    setSessionId(null);
    setMessages([WELCOME_MESSAGE]);
  };

  const ensureSession = async (): Promise<string | null> => {
    if (sessionId) return sessionId;
    if (creatingSessionRef.current) return null;

    // One session per day: if today's session already exists, reuse it rather than
    // creating another one.
    const todays = pastSessions.find((s) => isSameLocalDay(s.created_at));
    if (todays) {
      setSessionId(todays.session_id);
      return todays.session_id;
    }

    creatingSessionRef.current = true;
    try {
      const res = await startAgentSession(workspaceId, 'BRAIN');
      setSessionId(res.session_id);
      setPastSessions((prev) => {
        if (prev.some((s) => s.session_id === res.session_id)) return prev;
        return [{ session_id: res.session_id, created_at: new Date().toISOString() }, ...prev];
      });
      return res.session_id;
    } catch (err) {
      setInitError(err instanceof Error ? err.message : 'Failed to start session');
      return null;
    } finally {
      creatingSessionRef.current = false;
    }
  };

  const loadSession = async (id: string) => {
    setShowSessionPicker(false);
    setNudgeDismissed(false);
    setSessionId(id);
    setMessages([WELCOME_MESSAGE]);
    try {
      const { messages: history } = await getSessionHistory(workspaceId, id);
      if (history.length > 0) {
        const hydrated: BrainMessage[] = history.map((h) => {
          if (h.role !== 'assistant') {
            return { role: h.role, message: h.message };
          }
          const { message, actions: cardActions } = parseAgentOutput(h.message);
          return {
            role: 'assistant',
            message: normalizeAssistantMessage(message || h.message),
            cardActions,
          };
        });
        setMessages(hydrated);
      }
    } catch {
      // Keep default message if history fails
    }
  };

  const handleSend = async (text?: string) => {
    const userText = (text || input).trim();
    if (!userText || loading) return;

    const activeSessionId = await ensureSession();
    if (!activeSessionId) return;

    if (!text) setInput('');

    const userMsg: BrainMessage = { role: 'user', message: userText };
    const pendingMsg: BrainMessage = {
      role: 'assistant',
      message: '',
      status: 'pending',
    };
    setMessages((prev) => [...prev, userMsg, pendingMsg]);
    setLoading(true);

    try {
      const { job_id } = await sendSessionMessage(workspaceId, activeSessionId, userText);

      // The pending bubble needs the job id to poll its activity trail.
      setMessages((prev) => {
        const updated = [...prev];
        for (let i = updated.length - 1; i >= 0; i--) {
          if (updated[i].status === 'pending') {
            updated[i] = { ...updated[i], jobId: job_id };
            break;
          }
        }
        return updated;
      });

      pollJobStatus(
        job_id,
        getJobStatus,
        (jobStatus) => {
          const { message, actions: cardActions } = parseAgentOutput(jobStatus.output);
          const msg: BrainMessage = {
            role: 'assistant',
            message: normalizeAssistantMessage(message || 'Done!'),
            jobId: job_id,
            status: 'done',
            rawOutput: jobStatus.output,
            jobStatus,
            cardActions,
          };
          setMessages((prev) => {
            const updated = [...prev];
            // Find last pending message (iterate backwards for efficiency)
            for (let i = updated.length - 1; i >= 0; i--) {
              if (updated[i].status === 'pending') {
                updated[i] = msg;
                break;
              }
            }
            return updated;
          });
          setLoading(false);
        },
        (errorMsg) => {
          setMessages((prev) => {
            const updated = [...prev];
            for (let i = updated.length - 1; i >= 0; i--) {
              if (updated[i].status === 'pending') {
                updated[i] = { role: 'assistant', message: errorMsg, status: 'error' };
                break;
              }
            }
            return updated;
          });
          setLoading(false);
        }
      );
    } catch (err) {
      const errMsg = err instanceof Error ? err.message : 'Unknown error';
      setMessages((prev) => {
        const updated = [...prev];
        for (let i = updated.length - 1; i >= 0; i--) {
          if (updated[i].status === 'pending') {
            updated[i] = { role: 'assistant', message: errMsg, status: 'error' };
            break;
          }
        }
        return updated;
      });
      setLoading(false);
    }
  };

  const handleCardNavigate = (page: PageKey) => {
    navigate(PAGE_PATHS[page]);
  };

  if (initError) {
    return (
      <div className="ob-init-error">
        <div className="ob-init-error__icon">⚠️</div>
        <p className="ob-init-error__text">Couldn't start Brain session.</p>
        <p className="ob-init-error__detail">{initError}</p>
        <button
          className="ob-init-error__retry"
          onClick={() => {
            creatingSessionRef.current = false;
            setInitError(null);
            setSessionId(null);
          }}
        >
          Try again
        </button>
      </div>
    );
  }

  const showSuggestions = messages.length <= 1 && !loading;

  const userMessageCount = messages.filter((m) => m.role === 'user').length;
  const showSessionNudge =
    userMessageCount >= SESSION_MESSAGE_NUDGE_THRESHOLD && !nudgeDismissed;

  return (
    <div className="brain-page">
      {/* ── Command center header ── */}
      <div className="brain-header">
        <div className="brain-header__left">
          <div className="brain-header__avatar">
            <ConistraMark size={38} />
          </div>
          <div>
            <div className="brain-header__title">The Oracle</div>
            <div className="brain-header__subtitle">Senior strategist</div>
          </div>
        </div>
        <div className="brain-header__right">
          <div className={`brain-status ${sessionId ? 'live' : 'starting'}`}>
            <span className="brain-status__dot" />
            {sessionId ? 'Live' : 'Ready'}
          </div>
          {pastSessions.length > 0 && (
            <div className="brain-session-picker" ref={sessionPickerRef}>
              <button
                className="brain-session-btn"
                onClick={() => setShowSessionPicker((v) => !v)}
                aria-label="Session history"
              >
                History
                <span className="brain-session-btn__caret">▾</span>
              </button>
              {showSessionPicker && (
                <div className="brain-session-dropdown">
                  <div className="brain-session-dropdown__header">Past conversations</div>
                  {pastSessions.map((s) => (
                    <button
                      key={s.session_id}
                      className={`brain-session-item ${s.session_id === sessionId ? 'active' : ''}`}
                      onClick={() => loadSession(s.session_id)}
                    >
                      <span className="brain-session-item__time">
                        {formatSessionRelative(s.created_at)}
                      </span>
                      {s.session_id === sessionId && (
                        <span className="brain-session-item__check">✓</span>
                      )}
                    </button>
                  ))}
                  <div className="brain-session-dropdown__footer">
                    <button className="brain-session-new" onClick={startNewSession}>
                      + New conversation
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* ── Chat messages ── */}
      <div className="ob-chat-panel brain-chat-panel">
        {messages.map((msg, i) => (
          <div
            key={i}
            className={`ob-msg-row ob-msg-entry ${msg.role === 'user' ? 'user' : 'assistant'}`}
          >
            {msg.role === 'assistant' && (
              <div className="ob-avatar" aria-label="The Oracle">
                <ConistraMark size={20} variant="white" />
              </div>
            )}
            <div className="ob-msg-col">
              {msg.status === 'pending' ? (
                <div
                  className={`ob-bubble ob-bubble--assistant ob-bubble--pending${
                    activitySteps.length > 0 ? ' ob-bubble--trail' : ''
                  }`}
                  aria-live="polite"
                  aria-label="The Oracle is working"
                >
                  {activitySteps.length > 0 ? (
                    <div className="ob-feed">
                      {activitySteps.map((step) => (
                        <div
                          key={step.callId}
                          className={`ob-feed-item ${step.done ? 'ob-feed-item--done' : 'ob-feed-item--active'}`}
                        >
                          <span className="ob-feed-item__dot" />
                          <span className="ob-feed-item__label">{step.label}</span>
                          {!step.done && <span className="ob-feed-item__spinner" />}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <>
                      <div className="ob-typing-dots" aria-hidden="true">
                        <span />
                        <span />
                        <span />
                      </div>
                      <span className="ob-typing-context">The Oracle is thinking…</span>
                    </>
                  )}
                </div>
              ) : msg.status === 'error' ? (
                <div className="ob-bubble ob-bubble--error">
                  <span>⚠️ {msg.message}</span>
                  <button
                    className="ob-bubble-retry"
                    onClick={() => setMessages((prev) => prev.filter((_, idx) => idx !== i))}
                  >
                    Dismiss
                  </button>
                </div>
              ) : (
                <>
                  {msg.message && (
                    <div
                      className={`ob-bubble ${
                        msg.role === 'user' ? 'ob-bubble--user' : 'ob-bubble--assistant'
                      }`}
                    >
                      {msg.role === 'assistant' ? renderFormattedMessage(msg.message) : msg.message}
                    </div>
                  )}
                  {/* Render structured content cards from agent response */}
                  {msg.role === 'assistant' && (
                    <AgentCardList
                      actions={(msg as BrainMessage).cardActions || []}
                      onNavigate={handleCardNavigate}
                      onSend={handleSend}
                    />
                  )}
                  {msg.role === 'assistant' && msg.jobStatus && (
                    <button
                      className="chat-details-toggle"
                      onClick={() => setExpandedDetails((prev) => ({ ...prev, [i]: !prev[i] }))}
                    >
                      {expandedDetails[i] ? 'Hide details' : 'Show details'}
                    </button>
                  )}
                  {msg.role === 'assistant' && msg.jobStatus && expandedDetails[i] && (
                    <div className="chat-details">
                      <div>Job output</div>
                      <pre>
                        {JSON.stringify(msg.rawOutput ?? msg.jobStatus.output ?? null, null, 2)}
                      </pre>
                    </div>
                  )}
                </>
              )}
            </div>
            {msg.role === 'user' && (
              <div className="ob-avatar ob-avatar--user" aria-label="You">
                You
              </div>
            )}
          </div>
        ))}

        {/* Quick command suggestions — shown on fresh conversations */}
        {showSuggestions && (
          <div className="brain-suggestions">
            <div className="brain-suggestions__label">Quick commands</div>
            <div className="brain-suggestions__chips">
              {SUGGESTIONS.map((s, i) => (
                <button
                  key={i}
                  className="brain-suggestion-chip"
                  onClick={() => handleSend(s.text)}
                >
                  <span className="brain-suggestion-chip__icon">{s.icon}</span>
                  <span>{s.text}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* ── Long-session nudge: keep context tight by starting fresh ── */}
      {showSessionNudge && (
        <div className="brain-nudge" role="status">
          <span className="brain-nudge__icon" aria-hidden="true">
            💡
          </span>
          <span className="brain-nudge__text">
            This conversation is getting long. Starting a fresh one keeps the Oracle fast and
            focused.
          </span>
          <div className="brain-nudge__actions">
            <button className="brain-nudge__btn" onClick={startNewSession}>
              New conversation
            </button>
            <button
              className="brain-nudge__dismiss"
              onClick={() => setNudgeDismissed(true)}
              aria-label="Dismiss"
            >
              ✕
            </button>
          </div>
        </div>
      )}

      {/* ── Compose bar ── */}
      <div className="ob-compose">
        <textarea
          ref={inputRef}
          className="ob-compose__input"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key !== 'Enter') return;

            // Keep chat flow fast: Enter sends, modifier+Enter inserts newline.
            if (e.metaKey || e.ctrlKey || e.shiftKey) {
              return;
            }

            e.preventDefault();
            handleSend();
          }}
          placeholder={loading ? 'The Oracle is working on it…' : 'Ask the Oracle anything…'}
          disabled={loading}
          rows={1}
        />
        <button
          className="ob-compose__send"
          onClick={() => handleSend()}
          disabled={loading || !input.trim()}
          aria-label="Send message"
        >
          {loading ? <span className="ob-compose__send-spinner" /> : '→'}
        </button>
      </div>
    </div>
  );
}

export default AssistantChatPage;
